import { Select, Tooltip } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useWarehouseStore } from '@/store/warehouse-store';
import { warehouseApi } from '@/features/warehouses/api/warehouse-api';
import type { Warehouse } from '@/features/warehouses/types';

export function WarehouseSwitcher() {
    const { t } = useTranslation();
    const currentWarehouseId = useWarehouseStore((s) => s.currentWarehouseId);
    const hasUserInteracted = useWarehouseStore((s) => s.hasUserInteracted);
    const setCurrentWarehouseId = useWarehouseStore((s) => s.setCurrentWarehouseId);
    const seedDefaultWarehouseId = useWarehouseStore((s) => s.seedDefaultWarehouseId);
    const queryClient = useQueryClient();

    // Must return the full WarehousePageResponse object so this query matches the shape
    // other callers write to the same cache key (PurchaseOrderCreatePage, ReceiptParseModal,
    // PurchaseRequestAggregatedPage). If any of them runs its queryFn after ours, React
    // Query overwrites our cache entry with a { list, total, page, size } object. Returning
    // plain Warehouse[] here previously caused "o.map is not a function" because the
    // switcher next re-read the poisoned entry and tried to .map on an object.
    const { data: page } = useQuery({
        queryKey: ['warehouses', 'all'],
        queryFn: () => warehouseApi.getAll(0, 100),
        staleTime: 5 * 60 * 1000,
    });
    const warehouses: Warehouse[] = Array.isArray(page?.list) ? page.list : [];

    // First-visit seed: pick the OVERSEAS warehouse as the default the moment
    // the list loads, but ONLY if the user has never touched the control.
    // `hasUserInteracted` is set by `setCurrentWarehouseId` (i.e., a UI pick),
    // so users who have explicitly chosen "all" or another warehouse aren't
    // overridden. Done with the "adjust state during render" pattern (no
    // useEffect → setState chain) to keep the lint ratchet happy.
    if (
        !hasUserInteracted &&
        currentWarehouseId == null &&
        warehouses.length > 0
    ) {
        const overseas = warehouses.find((w) => w.type === 'OVERSEAS');
        if (overseas) seedDefaultWarehouseId(overseas.id);
    }

    const handleChange = (value: number | undefined) => {
        setCurrentWarehouseId(value ?? null);
        // Clear list/metric caches so UI refetches with the new filter
        queryClient.invalidateQueries();
    };

    return (
        <Tooltip title={t('warehouse_switcher.tooltip')}>
            <Select
                value={currentWarehouseId ?? undefined}
                onChange={handleChange}
                placeholder={t('warehouse_switcher.all')}
                allowClear
                suffixIcon={<HomeOutlined />}
                style={{ minWidth: 160 }}
                options={warehouses.map((w) => ({
                    value: w.id,
                    label: `${w.name}${w.type ? ` (${w.type})` : ''}`,
                }))}
            />
        </Tooltip>
    );
}
